import React, { useState, useRef, useEffect } from 'react';
import Icon from './Icon';

const MONTH_NAMES = ['January','February','March','April','May','June','July',
  'August','September','October','November','December'];
const DAY_HEADERS = ['MON','TUE','WED','THU','FRI','SAT','SUN'];
const ACCENT = '#FF3B30';

function toStr(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function buildGrid(year: number, month: number) {
  const first      = new Date(year, month, 1);
  const last       = new Date(year, month + 1, 0);
  const startOff   = (first.getDay() + 6) % 7; // Mon=0 … Sun=6
  const totalCells = Math.ceil((startOff + last.getDate()) / 7) * 7; // dynamic rows
  const cells: { date: Date; current: boolean }[] = [];
  for (let i = startOff - 1; i >= 0; i--)
    cells.push({ date: new Date(year, month, -i), current: false });
  for (let d = 1; d <= last.getDate(); d++)
    cells.push({ date: new Date(year, month, d), current: true });
  for (let d = 1; cells.length < totalCells; d++)
    cells.push({ date: new Date(year, month + 1, d), current: false });
  return cells;
}

interface DatePickerProps {
  value: string;            // YYYY-MM-DD
  onChange: (v: string) => void;
  min?: string;             // YYYY-MM-DD — dates before this are disabled
  max?: string;             // YYYY-MM-DD — dates after this are disabled
  disableWeekends?: boolean;
  /** 'input' = styled like a t-input (default), 'pill' = #f3f4f6 pill */
  variant?: 'input' | 'pill';
  placeholder?: string;
}

export default function DatePicker({
  value, onChange, min, max, disableWeekends = false,
  variant = 'input',
  placeholder = 'Select date',
}: DatePickerProps) {
  const todayStr  = toStr(new Date());
  const initYear  = value ? parseInt(value.slice(0,4))    : new Date().getFullYear();
  const initMonth = value ? parseInt(value.slice(5,7)) - 1 : new Date().getMonth();

  const [open, setOpen]     = useState(false);
  const [viewYear, setViewYear]   = useState(initYear);
  const [viewMonth, setViewMonth] = useState(initMonth);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  // Fixed-position coords so the calendar escapes overflow:auto modal containers
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left?: number; right?: number }>({});
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  useEffect(() => {
    if (!open) setShowMonthPicker(false);
  }, [open]);

  // Compute fixed viewport position each time it opens
  useEffect(() => {
    if (!open || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const CAL_H = 320;
    const CAL_W = 288;
    const spaceBelow = window.innerHeight - rect.bottom;
    const goUp = spaceBelow < CAL_H && rect.top >= CAL_H;
    const goRight = window.innerWidth - rect.left < CAL_W;
    setPos(
      goUp
        ? { bottom: window.innerHeight - rect.top + 6, ...(goRight ? { right: window.innerWidth - rect.right } : { left: rect.left }) }
        : { top: rect.bottom + 6,                      ...(goRight ? { right: window.innerWidth - rect.right } : { left: rect.left }) },
    );
  }, [open]);

  const prevMonth = () => viewMonth === 0
    ? (setViewMonth(11), setViewYear(y => y-1))
    : setViewMonth(m => m-1);
  const nextMonth = () => viewMonth === 11
    ? (setViewMonth(0), setViewYear(y => y+1))
    : setViewMonth(m => m+1);

  const isDisabled = (ds: string) => {
    if (!!min && ds < min) return true;
    if (!!max && ds > max) return true;
    if (disableWeekends) {
      const day = new Date(ds + 'T00:00:00').getDay();
      if (day === 0 || day === 6) return true;
    }
    return false;
  };

  const select = (ds: string) => {
    if (isDisabled(ds)) return;
    onChange(ds);
    setOpen(false);
  };

  const goToday = () => {
    if (isDisabled(todayStr)) return;
    const t = new Date();
    setViewYear(t.getFullYear()); setViewMonth(t.getMonth());
    select(todayStr);
  };

  const display = value
    ? new Date(value + 'T00:00:00').toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })
    : '';

  const cells = buildGrid(viewYear, viewMonth);

  // ── Trigger style ────────────────────────────────────────────────────────
  const triggerPill: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box',
    paddingLeft: 16, paddingRight: 48, paddingTop: 14, paddingBottom: 14,
    background: '#f3f4f6',
    border: `1.5px solid ${open ? 'var(--accent,#3b82f6)' : 'transparent'}`,
    borderRadius: 14, fontSize: 15, cursor: 'pointer',
    color: value ? 'var(--text,#1f2937)' : '#9ca3af',
    display: 'flex', alignItems: 'center', userSelect: 'none',
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      {/* Trigger */}
      {variant === 'pill' ? (
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }} onClick={() => setOpen(o => !o)}>
          <div style={triggerPill}>{display || placeholder}</div>
          <span style={{ position: 'absolute', right: 16, pointerEvents: 'none', display: 'flex' }}>
            <Icon name="calendar" size={18} color="#9ca3af" />
          </span>
        </div>
      ) : (
        <div
          className="t-input"
          onClick={() => setOpen(o => !o)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            cursor: 'pointer', userSelect: 'none',
            color: value ? 'var(--text)' : 'var(--text-muted)',
            outline: open ? '2px solid var(--accent,#3b82f6)' : undefined,
          }}
        >
          <span>{display || placeholder}</span>
          <Icon name="calendar" size={16} color="var(--text-muted,#9ca3af)" />
        </div>
      )}

      {/* Calendar dropdown — position:fixed escapes overflow:auto modal containers */}
      {open && (
        <div style={{
          position: 'fixed', zIndex: 9000,
          ...pos,
          width: 300,
          backgroundColor: '#fff',
          border: '1px solid rgba(0,0,0,0.05)', borderRadius: 24,
          boxShadow: '0 10px 36px rgba(0,0,0,0.18)',
          padding: 18,
        }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <button
              type="button"
              onClick={() => setShowMonthPicker(s => !s)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                display: 'flex', alignItems: 'center', gap: 4,
                fontWeight: 700, fontSize: 16, color: ACCENT,
              }}
            >
              <span>{MONTH_NAMES[viewMonth]} {viewYear}</span>
              <span style={{
                display: 'flex', transition: 'transform .2s',
                transform: showMonthPicker ? 'rotate(180deg)' : 'rotate(0deg)',
              }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
              </span>
            </button>
            <div style={{ display: 'flex', gap: 2 }}>
              {[
                { d: prevMonth, path: 'M15 18l-6-6 6-6' },
                { d: nextMonth, path: 'M9 18l6-6-6-6' },
              ].map(({ d, path }, i) => (
                <button key={i} type="button" onClick={d} style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  width: 28, height: 28, borderRadius: 7, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', color: ACCENT,
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d={path}/>
                  </svg>
                </button>
              ))}
            </div>
          </div>

          {/* Day headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', marginBottom: 4 }}>
            {DAY_HEADERS.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, letterSpacing: 0.5, color: '#9ca3af', paddingBottom: 5, overflow: 'hidden' }}>{d}</div>
            ))}
          </div>

          {/* Date cells & month/year overlay */}
          <div style={{ position: 'relative', minHeight: 216 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', rowGap: 2 }}>
              {cells.map(({ date, current }, i) => {
                const ds         = toStr(date);
                const isToday    = ds === todayStr;
                const isSelected = ds === value;
                const disabled   = isDisabled(ds);
                return (
                  <div
                    key={i}
                    onClick={() => select(ds)}
                    style={{
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.35 : 1,
                      padding: '1px 0',
                    }}
                  >
                    <span style={{
                      width: 30, height: 30,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      borderRadius: '50%', fontSize: 13,
                      fontWeight: isSelected ? 600 : 500,
                      background: isSelected ? ACCENT : 'transparent',
                      boxShadow: isSelected ? '0 2px 6px rgba(255,59,48,0.35)' : undefined,
                      color: isSelected ? '#fff' : !current ? '#d1d5db' : ACCENT,
                      flexShrink: 0,
                    }}>
                      {date.getDate()}
                    </span>
                    {isToday && !isSelected && (
                      <span style={{
                        width: 3, height: 3, borderRadius: '50%', marginTop: 1,
                        background: ACCENT,
                        display: 'block', flexShrink: 0,
                      }} />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Month/Year picker overlay */}
            {showMonthPicker && (
              <div style={{
                position: 'absolute', inset: 0, zIndex: 10,
                display: 'flex', flexDirection: 'column',
                background: 'rgba(255,255,255,0.97)', borderRadius: 18, padding: 10,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid rgba(0,0,0,0.05)',
                }}>
                  <button type="button" onClick={() => setViewYear(y => y - 1)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', color: ACCENT,
                    width: 26, height: 26, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
                  </button>
                  <span style={{ fontWeight: 700, fontSize: 15, color: '#1f2937' }}>{viewYear}</span>
                  <button type="button" onClick={() => setViewYear(y => y + 1)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', color: ACCENT,
                    width: 26, height: 26, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, flex: 1 }}>
                  {MONTH_NAMES.map((m, idx) => {
                    const isSelectedMonth = idx === viewMonth;
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => { setViewMonth(idx); setShowMonthPicker(false); }}
                        style={{
                          border: 'none', cursor: 'pointer', padding: '7px 0', borderRadius: 9,
                          fontSize: 12, fontWeight: 700,
                          background: isSelectedMonth ? ACCENT : 'transparent',
                          color: isSelectedMonth ? '#fff' : '#1f2937',
                        }}
                      >
                        {m.slice(0, 3)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(0,0,0,0.05)',
          }}>
            <button type="button" onClick={() => { onChange(''); setOpen(false); }} style={{
              background: 'none', border: 'none', color: ACCENT,
              fontWeight: 600, cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', gap: 5,
            }}>
              <Icon name="calendar" size={13} color={ACCENT} />
              Clear
            </button>
            <button type="button" onClick={goToday} style={{
              background: 'none', border: 'none', color: isDisabled(todayStr) ? '#9ca3af' : ACCENT,
              fontWeight: 600, cursor: isDisabled(todayStr) ? 'not-allowed' : 'pointer', fontSize: 13,
            }}>
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
